import type { FC } from "react";
import React from "react";

import { z } from "@core/zod";

import { useZodForm } from "@react/foundation/react-hook-form";
import { Button } from "@react/ui-primitives/button";
import { FieldError } from "@react/ui-primitives/field";
import { Input } from "@react/ui-primitives/input";
import {
  NativeSelect,
  NativeSelectOption,
} from "@react/ui-primitives/native-select";

import type { MediaAssetVisibility } from "@apis/media/models";
import { MediaAssetVisibilitySchema } from "@apis/media/models";

import { useMediaUpload } from "../../../hooks/useMediaUpload.ts";

const UploadFormSchema = z.object(
  { file: z.instanceof(File), visibility: MediaAssetVisibilitySchema },
  {
    file: "The image file to upload",
    visibility: "The visibility of the media asset the upload creates",
  },
);

const defaultValues: {
  readonly file?: FileList;
  readonly visibility: MediaAssetVisibility;
} = { file: undefined, visibility: "private" };

export const UploadForm: FC = () => {
  const upload = useMediaUpload();
  const form = useZodForm({
    defaultValues,
    errorMapping: { file: { invalid: "Select an image to upload." } },
    onSubmit: async ({ file, visibility }) => {
      await upload.mutateAsync(
        { file, visibility },
        {
          onSuccess: () => {
            form.reset();
          },
        },
      );
    },
    schema: UploadFormSchema,
    transform: values => {
      return { file: values.file?.[0], visibility: values.visibility };
    },
  });

  return (
    <form className="flex flex-col gap-2" onSubmit={form.handleSubmit}>
      <div className="flex items-center gap-3">
        <Input
          accept="image/*"
          className="max-w-sm"
          type="file"
          {...form.register("file")}
        />
        <NativeSelect {...form.register("visibility")}>
          <NativeSelectOption value="private">Private</NativeSelectOption>
          <NativeSelectOption value="public">Public</NativeSelectOption>
        </NativeSelect>
        <Button
          disabled={form.state.isSubmitting || !form.state.isValid}
          type="submit"
        >
          {form.state.isSubmitting ? "Uploading…" : "Upload"}
        </Button>
      </div>
      <FieldError errors={[form.state.errors.file]} />
    </form>
  );
};
